import type { UsageGroupBy, UsageRow } from '@agent-platform/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { COST_ESTIMATE_NOTE, costCell, formatCompact, formatHitRate } from '@/lib/format';

const GROUPS: Record<UsageGroupBy, string> = {
  day: 'Day',
  agent: 'Agent',
  apiKey: 'API key',
  model: 'Model',
};
const RANGES = { '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days' } as const;
type Range = keyof typeof RANGES;

const number = new Intl.NumberFormat();

function rowLabel(row: UsageRow, groupBy: UsageGroupBy): string {
  if (groupBy === 'apiKey' && row.key === null) return 'Playground (no key)';
  // A grouped agent or key without a name has been deleted since; its runs still count.
  if (row.label === null && row.key && (groupBy === 'agent' || groupBy === 'apiKey'))
    return `Deleted ${groupBy === 'agent' ? 'agent' : 'API key'} · ${row.key.slice(0, 8)}`;
  return row.label ?? row.key ?? '—';
}

/** Horizontal bars of total tokens, split into input and output. */
function UsageBars({ rows, groupBy }: { rows: UsageRow[]; groupBy: UsageGroupBy }) {
  const max = Math.max(1, ...rows.map((r) => r.inputTokens + r.outputTokens));
  return (
    <ul className="grid gap-2" aria-label="Token usage chart">
      {rows.map((row) => {
        const total = row.inputTokens + row.outputTokens;
        const label = rowLabel(row, groupBy);
        const cost = costCell(row.costUsd, row.unpricedRuns);
        return (
          <li
            key={row.key ?? 'none'}
            className="grid grid-cols-[10rem_1fr_6rem] items-center gap-3 text-sm"
            data-testid="usage-bar"
          >
            <span className="truncate" title={`${label} · ${cost.text}`}>
              {label}
              <span
                className="text-muted-foreground ml-1.5 text-xs"
                title={cost.title ?? COST_ESTIMATE_NOTE}
              >
                {cost.text}
              </span>
            </span>
            <div
              className="bg-muted flex h-4 overflow-hidden rounded"
              role="img"
              aria-label={`${label}: ${number.format(row.inputTokens)} input and ${number.format(row.outputTokens)} output tokens`}
            >
              <div className="bg-primary" style={{ width: `${(row.inputTokens / max) * 100}%` }} />
              <div
                className="bg-primary/50"
                style={{ width: `${(row.outputTokens / max) * 100}%` }}
              />
            </div>
            <span
              className="text-muted-foreground text-right text-xs tabular-nums"
              title={`${number.format(total)} tokens`}
            >
              {formatCompact(total)} tok
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function UsageContent({ tenantId }: { tenantId: string }) {
  const [groupBy, setGroupBy] = useState<UsageGroupBy>('day');
  const [range, setRange] = useState<Range>('30');
  const usage = useQuery({
    queryKey: ['usage', tenantId, groupBy, range],
    queryFn: () => {
      // Whole UTC days, so the window does not shift on every refetch.
      const from = new Date();
      from.setUTCHours(0, 0, 0, 0);
      from.setUTCDate(from.getUTCDate() - Number(range) + 1);
      const params = new URLSearchParams({ groupBy, from: from.toISOString() });
      return api<{ items: UsageRow[] }>(`/usage?${params}`);
    },
  });
  const rows = usage.data?.items ?? [];
  const totals = rows.reduce(
    (t, r) => ({
      runs: t.runs + r.runs,
      inputTokens: t.inputTokens + r.inputTokens,
      outputTokens: t.outputTokens + r.outputTokens,
      cacheReadTokens: t.cacheReadTokens + r.cacheReadTokens,
      cacheWriteTokens: t.cacheWriteTokens + r.cacheWriteTokens,
      costUsd: t.costUsd + r.costUsd,
      unpricedRuns: t.unpricedRuns + r.unpricedRuns,
    }),
    {
      runs: 0,
      inputTokens: 0,
      outputTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
      costUsd: 0,
      unpricedRuns: 0,
    },
  );
  const totalCost = costCell(totals.costUsd, totals.unpricedRuns);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="usage-group" className="text-muted-foreground text-xs">
            Group by
          </Label>
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as UsageGroupBy)}>
            <SelectTrigger id="usage-group" aria-label="Group by" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(GROUPS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="usage-range" className="text-muted-foreground text-xs">
            Period
          </Label>
          <Select value={range} onValueChange={(v) => setRange(v as Range)}>
            <SelectTrigger id="usage-range" aria-label="Period" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(RANGES).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <ErrorAlert error={usage.error} />
      <div className="grid grid-cols-5 gap-4">
        {(
          [
            ['Runs', totals.runs, false],
            ['Input tokens', totals.inputTokens, true],
            ['Output tokens', totals.outputTokens, true],
          ] as const
        ).map(([label, value, tokens]) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-muted-foreground text-sm font-normal">{label}</CardTitle>
            </CardHeader>
            <CardContent
              className="text-2xl font-semibold tabular-nums"
              data-testid={`usage-total-${label}`}
              title={tokens ? `${number.format(value)} tokens` : undefined}
            >
              {tokens ? formatCompact(value) : number.format(value)}
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-normal">
              Cache hit rate
            </CardTitle>
          </CardHeader>
          <CardContent
            className="text-2xl font-semibold tabular-nums"
            data-testid="usage-total-Cache hit rate"
            title={`${number.format(totals.cacheReadTokens)} of ${number.format(totals.inputTokens)} input tokens read from cache`}
          >
            {totals.cacheReadTokens + totals.cacheWriteTokens > 0
              ? formatHitRate(totals.cacheReadTokens, totals.inputTokens)
              : '—'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-muted-foreground text-sm font-normal">Cost</CardTitle>
          </CardHeader>
          <CardContent
            className="text-2xl font-semibold tabular-nums"
            data-testid="usage-total-Cost"
            title={totalCost.title ?? COST_ESTIMATE_NOTE}
          >
            {totalCost.text}
          </CardContent>
        </Card>
      </div>
      {usage.isPending ? (
        <Skeleton className="h-40" />
      ) : rows.length === 0 ? (
        <EmptyState title="No usage in this period">
          Token usage is recorded for every run that reports it.
        </EmptyState>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tokens by {GROUPS[groupBy].toLowerCase()}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6">
            <UsageBars rows={rows} groupBy={groupBy} />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{GROUPS[groupBy]}</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead className="text-right">Input tokens</TableHead>
                  <TableHead className="text-right">Output tokens</TableHead>
                  <TableHead className="text-right">Cached</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key ?? 'none'}>
                    <TableCell>{rowLabel(row, groupBy)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {number.format(row.runs)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title={number.format(row.inputTokens)}
                    >
                      {formatCompact(row.inputTokens)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title={number.format(row.outputTokens)}
                    >
                      {formatCompact(row.outputTokens)}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title={`${number.format(row.cacheReadTokens)} cached input tokens`}
                    >
                      {row.cacheReadTokens + row.cacheWriteTokens > 0
                        ? formatHitRate(row.cacheReadTokens, row.inputTokens)
                        : '—'}
                    </TableCell>
                    <TableCell
                      className="text-right tabular-nums"
                      title={costCell(row.costUsd, row.unpricedRuns).title ?? COST_ESTIMATE_NOTE}
                    >
                      {costCell(row.costUsd, row.unpricedRuns).text}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function UsagePage() {
  return (
    <>
      <PageHeader
        title="Usage"
        description="Runs and tokens across the playground and endpoints (times in UTC)."
      />
      <RequireTenant>{(tenant) => <UsageContent tenantId={tenant.id} />}</RequireTenant>
    </>
  );
}
